import { Gauge } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmActionTelemetry } from "@/hooks/use-crm-action-telemetry";

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "Unknown";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

interface ActionTelemetrySnapshotProps {
  contractRef?: string;
  signalFamily?: string;
  sourceEventType?: string;
  onSignalFamilySelect?: (signalFamily: string) => void;
  onSourceEventTypeSelect?: (sourceEventType: string, signalFamily: string) => void;
}

export function ActionTelemetrySnapshot({
  contractRef,
  signalFamily,
  sourceEventType,
  onSignalFamilySelect,
  onSourceEventTypeSelect,
}: ActionTelemetrySnapshotProps = {}) {
  const telemetryFilters = {
    ...(contractRef ? { actionRef: contractRef } : {}),
    ...(signalFamily ? { signalFamily } : {}),
    ...(sourceEventType ? { sourceEventType } : {}),
  };
  const { data, isLoading, error, refetch } = useCrmActionTelemetry(telemetryFilters);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-52" />
        </div>
        <SkeletonCard className="h-20" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
        Could not load action telemetry.
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  const decisions = data?.decisions ?? { accepted: 0, dismissed: 0, total: 0 };
  const effectiveness = data?.effectiveness ?? {
    reviewed: 0,
    accepted: 0,
    dismissed: 0,
    materialized: 0,
    crmMaterialized: 0,
    manualResolved: 0,
    acceptanceRate: 0,
    materializationRate: 0,
    crmMaterializationRate: 0,
    manualResolutionRate: 0,
    avgTimeToMaterializeMinutes: null,
    lastReviewedAt: null,
    lastAcceptedAt: null,
    lastMaterializedAt: null,
  };
  const queue = data?.queue ?? {
    accepted: 0,
    materialized: 0,
    readyForExecution: 0,
    manualReview: 0,
    blocked: 0,
    unknown: 0,
  };
  const topSurface = data?.bySourceSurface[0];
  const topSignalFamily = data?.bySignalFamily?.[0];
  const topSourceEventType = data?.bySourceEventType?.[0];
  const topOutcome = data?.topOutcomes[0];
  const automation = data?.automation ?? {
    reviewed: 0,
    accepted: 0,
    dismissed: 0,
    materialized: 0,
    crmMaterialized: 0,
    manualResolved: 0,
    acceptanceRate: 0,
    materializationRate: 0,
    crmMaterializationRate: 0,
    manualResolutionRate: 0,
    avgTimeToMaterializeMinutes: null,
    lastReviewedAt: null,
    lastAcceptedAt: null,
    lastMaterializedAt: null,
  };
  const staff = data?.staff ?? {
    reviewed: 0,
    accepted: 0,
    dismissed: 0,
    materialized: 0,
    crmMaterialized: 0,
    manualResolved: 0,
    acceptanceRate: 0,
    materializationRate: 0,
    crmMaterializationRate: 0,
    manualResolutionRate: 0,
    avgTimeToMaterializeMinutes: null,
    lastReviewedAt: null,
    lastAcceptedAt: null,
    lastMaterializedAt: null,
  };

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Action Telemetry Snapshot</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Consolidated action review, queue, and outcome signals for the CRM action loop.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {data?.windowDays ?? 30}d
        </span>
      </div>

      {contractRef && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Telemetry scoped to {prettyActionLabel(contractRef)}
        </p>
      )}
      {(signalFamily || sourceEventType) && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Signal scope {[
            signalFamily ? `${prettyActionLabel(signalFamily)} family` : null,
            sourceEventType ? prettyActionLabel(sourceEventType) : null,
          ].filter(Boolean).join(" · ")}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Reviewed</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{decisions.total}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-3">
          <p className="text-xs text-[var(--color-success)]">Accepted</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-success)]">{percent(effectiveness.acceptanceRate)}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
          <p className="text-xs text-[var(--color-primary)]">Materialized</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-primary)]">{percent(effectiveness.materializationRate)}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {effectiveness.crmMaterialized} CRM · {effectiveness.manualResolved} manual
          </p>
        </div>
        <div className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-3">
          <p className="text-xs text-[var(--color-warning)]">Ready queue</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-warning)]">{queue.readyForExecution}</p>
        </div>
      </div>

      {(automation.reviewed > 0 || staff.reviewed > 0) && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
            <p className="text-xs text-[var(--color-muted-foreground)]">Staff reviewed</p>
            <p className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{staff.reviewed}</p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {staff.accepted} accepted · {staff.materialized} materialized
            </p>
          </div>
          <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
            <p className="text-xs text-[var(--color-primary)]">Automation reviewed</p>
            <p className="mt-1 text-lg font-semibold text-[var(--color-primary)]">{automation.reviewed}</p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {automation.accepted} accepted · {automation.materialized} materialized
            </p>
          </div>
        </div>
      )}

      {(topSurface || topSignalFamily || topSourceEventType || topOutcome) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {topSignalFamily && (
            <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
              <p className="text-xs text-[var(--color-primary)]">Top signal family</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{prettyActionLabel(topSignalFamily.signalFamily)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSignalFamily.materialized}/{topSignalFamily.accepted} accepted materialized
              </p>
              {onSignalFamilySelect && (
                <button
                  type="button"
                  onClick={() => onSignalFamilySelect(topSignalFamily.signalFamily)}
                  className="mt-2 rounded-md border border-[var(--color-primary)]/25 px-2 py-1 text-xs font-medium text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary)]/10"
                >
                  Filter queue
                </button>
              )}
            </div>
          )}
          {topSourceEventType && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Top source event</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{prettyActionLabel(topSourceEventType.sourceEventType)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {percent(topSourceEventType.materializationRate)} materialized · {prettyActionLabel(topSourceEventType.signalFamily)} family
              </p>
              {onSourceEventTypeSelect && (
                <button
                  type="button"
                  onClick={() => onSourceEventTypeSelect(topSourceEventType.sourceEventType, topSourceEventType.signalFamily)}
                  className="mt-2 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                >
                  Filter queue
                </button>
              )}
            </div>
          )}
          {topSurface && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Best source surface</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{prettyActionLabel(topSurface.sourceSurface)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSurface.materialized}/{topSurface.accepted} accepted materialized
              </p>
            </div>
          )}
          {topOutcome && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Top outcome-backed action</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{prettyActionLabel(topOutcome.actionRef)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {percent(topOutcome.materializationRate)} materialized, {topOutcome.confidence} confidence
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
